<template>
  <div class="registration-form">
    <loading :active.sync="loading" :is-full-page="true" />

    <form v-if="step === 'details'" @submit.prevent="submitForm">
      <div v-if="Object.keys(errors).length" class="alert alert-danger" role="alert">
        <strong>Please correct the following errors:</strong>
        <ul class="mb-0 mt-2">
          <li v-for="(msgs, field) in errors" :key="field">
            {{ msgs[0] }}
          </li>
        </ul>
      </div>

      <p class="form-intro text-muted">Fill in your details below. Fields marked with <span class="text-danger">*</span> are required.</p>

      <section class="reg-section">
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>Name <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': hasError('name') }"
                v-model="form.name"
                placeholder="Enter your full name"
                required
                @input="clearError('name')"
              />
              <div v-if="hasError('name')" class="invalid-feedback d-block">{{ getError('name') }}</div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label>E-mail Address <span class="text-danger">*</span></label>
              <input
                type="email"
                class="form-control"
                :class="{ 'is-invalid': hasError('email') }"
                v-model="form.email"
                placeholder="you@example.com"
                required
                @input="clearError('email')"
              />
              <div v-if="hasError('email')" class="invalid-feedback d-block">{{ getError('email') }}</div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label>Mobile Number <span class="text-danger">*</span></label>
              <div class="input-group">
                <div class="input-group-prepend"><span class="input-group-text">+91</span></div>
                <input
                  type="tel"
                  class="form-control"
                  :class="{ 'is-invalid': hasError('phone_no') }"
                  v-model="form.phone_no"
                  placeholder="10-digit mobile number"
                  maxlength="10"
                  pattern="[0-9]{10}"
                  required
                  @input="clearError('phone_no')"
                />
              </div>
              <small class="form-text text-muted">OTP will be sent to this number for verification.</small>
              <div v-if="hasError('phone_no')" class="invalid-feedback d-block">{{ getError('phone_no') }}</div>
            </div>
          </div>
        </div>
      </section>

      <section class="reg-section">
        <h5 class="reg-section-heading">Institutional Details</h5>
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>Name of University <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': hasError('university_name') }"
                v-model="form.university_name"
                placeholder="Enter your university name"
                required
                @input="clearError('university_name')"
              />
              <div v-if="hasError('university_name')" class="invalid-feedback d-block">{{ getError('university_name') }}</div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label>Programme <span class="text-danger">*</span></label>
              <select
                class="form-control"
                v-model="form.programme"
                required
                @change="clearError('programme')"
                :class="{ 'is-invalid': hasError('programme') }"
              >
                <option value="">Select programme</option>
                <option value="ballb">BALLB</option>
                <option value="bba_llb">BBA LLB</option>
                <option value="bcom_llb">BCOM LLB</option>
                <option value="llb">LLB</option>
              </select>
              <div v-if="hasError('programme')" class="invalid-feedback d-block">{{ getError('programme') }}</div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label>Year / Semester of Study <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': hasError('year_semester_of_study') }"
                v-model="form.year_semester_of_study"
                placeholder="e.g. 3rd Year / 5th Semester"
                required
                @input="clearError('year_semester_of_study')"
              />
              <div v-if="hasError('year_semester_of_study')" class="invalid-feedback d-block">{{ getError('year_semester_of_study') }}</div>
            </div>
          </div>
        </div>
      </section>

      <section class="reg-section">
        <h5 class="reg-section-heading">Paper Details</h5>
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>Title of Paper <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': hasError('title_of_paper') }"
                v-model="form.title_of_paper"
                placeholder="Enter the title of your paper"
                required
                @input="clearError('title_of_paper')"
              />
              <div v-if="hasError('title_of_paper')" class="invalid-feedback d-block">{{ getError('title_of_paper') }}</div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label>Name of Co-Authors <span class="optional-tag">(optional)</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': hasError('name_of_co_author') }"
                v-model="form.name_of_co_author"
                placeholder="Enter co-author name(s)"
                @input="clearError('name_of_co_author')"
              />
              <div v-if="hasError('name_of_co_author')" class="invalid-feedback d-block">{{ getError('name_of_co_author') }}</div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label>Institution of Co-Authors <span class="optional-tag">(optional)</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': hasError('institution_of_co_authors') }"
                v-model="form.institution_of_co_authors"
                placeholder="Enter institution of co-author(s)"
                @input="clearError('institution_of_co_authors')"
              />
              <div v-if="hasError('institution_of_co_authors')" class="invalid-feedback d-block">{{ getError('institution_of_co_authors') }}</div>
            </div>
          </div>
        </div>
      </section>

      <section class="reg-section">
        <h5 class="reg-section-heading">Certificate Details</h5>
        <p class="reg-section-sub text-muted">These details will be printed on your certificate.</p>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>Name to appear on Certificate <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': hasError('certificate_name') }"
                v-model="form.certificate_name"
                placeholder="Name as it should appear"
                required
                @input="clearError('certificate_name')"
              />
              <div v-if="hasError('certificate_name')" class="invalid-feedback d-block">{{ getError('certificate_name') }}</div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label>Affiliation to appear on Certificate <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': hasError('certificate_affiliation') }"
                v-model="form.certificate_affiliation"
                placeholder="Affiliation as it should appear"
                required
                @input="clearError('certificate_affiliation')"
              />
              <div v-if="hasError('certificate_affiliation')" class="invalid-feedback d-block">{{ getError('certificate_affiliation') }}</div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="eventConfig.accepts_abstract" class="reg-section">
        <h5 class="reg-section-heading">Abstract Upload</h5>
        <div class="form-group mb-0">
          <label>Upload Abstract (PDF) <span v-if="eventConfig.abstract_required" class="text-danger">*</span></label>
          <div class="file-upload-wrap">
            <input
              type="file"
              accept="application/pdf,.pdf"
              class="form-control-file"
              :required="eventConfig.abstract_required"
              @change="abstractFile = $event.target.files[0] || null"
            >
          </div>
          <small class="form-text text-muted">PDF only, maximum 2 MB.</small>
          <div v-if="abstractFile" class="selected-file">
            <i class="fa fa-file-pdf-o"></i> {{ abstractFile.name }}
          </div>
          <div v-if="hasError('abstract_file')" class="text-danger mt-1">{{ getError('abstract_file') }}</div>
        </div>
      </section>

      <section v-if="pricingList.length" class="reg-section">
        <h5 class="reg-section-heading">Registration Fee</h5>
        <p class="reg-section-sub text-muted">Select your category to view the applicable fee.</p>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>Category <span class="text-danger">*</span></label>
              <select
                class="form-control"
                v-model="selectedCategory"
                required
                @change="clearError('pricing_id')"
                :class="{ 'is-invalid': hasError('pricing_id') }"
              >
                <option value="">Select category</option>
                <option v-for="cat in categories" :key="cat" :value="cat">
                  {{ cat }}
                </option>
              </select>
              <div v-if="hasError('pricing_id')" class="invalid-feedback d-block">{{ getError('pricing_id') }}</div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label>Sub Category <span class="text-danger">*</span></label>
              <select
                class="form-control"
                v-model="selectedSubCategory"
                :disabled="!selectedCategory"
                required
                @change="clearError('pricing_id')"
                :class="{ 'is-invalid': hasError('pricing_id') }"
              >
                <option value="">Select sub category</option>
                <option v-for="sub in subCategories" :key="sub" :value="sub">
                  {{ sub }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="selectedPricing" class="pricing-breakup">
          <template v-if="isFreePricing">
            <div class="pricing-row pricing-row-total">
              <span>Total Payable</span>
              <strong class="text-success">Free</strong>
            </div>
          </template>
          <template v-else>
            <div class="pricing-row">
              <span>Base Amount</span>
              <strong>₹{{ baseAmount }}</strong>
            </div>
            <div v-if="isGstApplicable" class="pricing-row">
              <span>GST ({{ gstRate }}%)</span>
              <strong>₹{{ gstAmount }}</strong>
            </div>
            <div class="pricing-row pricing-row-total">
              <span>Total Payable</span>
              <strong>₹{{ totalAmount }}</strong>
            </div>
          </template>
          <p v-if="selectedPricing.notes" class="pricing-notes">{{ selectedPricing.notes }}</p>
        </div>
      </section>

      <section class="reg-section reg-section-last">
        <div class="captcha-wrap">
          <div
            class="g-recaptcha"
            :data-sitekey="captchaKey"
            data-callback="onCaptchaVerified"
          ></div>
          <small v-if="captchaError" class="text-danger d-block mt-2">
            Please verify that you are not a robot.
          </small>
        </div>

        <div class="submit-wrap">
          <button type="submit" class="btn-verify-primary">
            <i class="fa fa-check-circle"></i> Submit Registration
          </button>
        </div>
      </section>
    </form>

    <section v-else class="verify-flow">
      <div class="verify-progress">
        <div class="verify-progress-step" :class="{ done: step !== 'details', active: step === 'email' }">
          <span class="step-dot"><i v-if="step !== 'details' && step !== 'email'" class="fa fa-check"></i><template v-else>1</template></span>
          <span class="step-label">Email</span>
        </div>
        <div class="verify-progress-line" :class="{ done: step === 'phone' }"></div>
        <div class="verify-progress-step" :class="{ active: step === 'delivery' || step === 'phone' }">
          <span class="step-dot">2</span>
          <span class="step-label">Mobile</span>
        </div>
      </div>

      <template v-if="step === 'email' || step === 'phone'">
        <div class="verify-card">
          <div class="verify-icon" :class="step === 'email' ? 'verify-icon-email' : 'verify-icon-phone'">
            <i :class="step === 'email' ? 'fa fa-envelope' : 'fa fa-mobile'"></i>
          </div>
          <h3 class="verify-title">{{ step === 'email' ? 'Verify your email' : 'Enter verification code' }}</h3>
          <p class="verify-subtitle">
            <template v-if="step === 'email'">
              We sent a 4-digit code to <strong>{{ maskedEmail }}</strong>
            </template>
            <template v-else>
              Enter the code sent via <strong>{{ deliveryLabel }}</strong> to <strong>******{{ form.phone_no.slice(-4) }}</strong>
            </template>
          </p>

          <div class="otp-inputs" @paste.prevent="pasteOtp">
            <input
              v-for="(digit,index) in otpDigits"
              :key="index"
              :ref="'otp'+index"
              :value="digit"
              maxlength="1"
              inputmode="numeric"
              class="otp-box"
              :disabled="loading || expired"
              @input="inputOtp(index,$event)"
              @keydown="otpKeydown(index,$event)"
            >
          </div>

          <div class="verify-timer" :class="{ expired: expired }">
            <i class="fa fa-clock-o"></i>
            {{ expired ? 'Code expired' : 'Expires in ' + formattedTime }}
          </div>

          <div v-if="otpError" class="verify-alert">{{ otpError }}</div>

          <button
            type="button"
            class="btn-verify-primary"
            :disabled="otpCode.length !== 4 || loading || expired"
            @click="verifyOtp"
          >
            <span v-if="loading"><i class="fa fa-spinner fa-spin"></i> Verifying...</span>
            <span v-else>Verify {{ step === 'email' ? 'Email' : 'Number' }}</span>
          </button>

          <div class="verify-actions">
            <button type="button" class="btn-verify-link" :disabled="!expired || cooldownRemaining > 0 || loading" @click="resendOtp">
              {{ resendLabel }}
            </button>
            <button v-if="step === 'phone'" type="button" class="btn-verify-link" :disabled="loading" @click="step='delivery'">
              Change delivery method
            </button>
          </div>
        </div>
      </template>

      <template v-else-if="step === 'delivery'">
        <div class="verify-card">
          <div class="verify-icon verify-icon-phone">
            <i class="fa fa-mobile"></i>
          </div>
          <h3 class="verify-title">Verify your mobile number</h3>
          <p class="verify-subtitle">
            Choose how you would like to receive the OTP on <strong>******{{ form.phone_no.slice(-4) }}</strong>
          </p>

          <div class="delivery-grid">
            <button
              type="button"
              class="delivery-card"
              :class="{ active: deliveryMethod === 'whatsapp' }"
              @click="deliveryMethod = 'whatsapp'"
            >
              <span class="delivery-card-icon whatsapp"><i class="fab fa-whatsapp" aria-hidden="true"></i></span>
              <span class="delivery-card-title">WhatsApp</span>
              <span class="delivery-card-desc">Recommended for faster delivery</span>
            </button>

            <button
              type="button"
              class="delivery-card"
              :class="{ active: deliveryMethod === 'sms' }"
              @click="deliveryMethod = 'sms'"
            >
              <span class="delivery-card-icon sms"><i class="fa fa-comment"></i></span>
              <span class="delivery-card-title">SMS</span>
              <span class="delivery-card-desc">Standard text message</span>
            </button>
          </div>

          <div v-if="otpError" class="verify-alert">{{ otpError }}</div>

          <button type="button" class="btn-verify-primary" :disabled="loading" @click="sendPhoneOtp">
            <span v-if="loading"><i class="fa fa-spinner fa-spin"></i> Sending...</span>
            <span v-else>Send OTP</span>
          </button>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    captchaKey: {
      type: String,
      required: true,
    },
    pricingApi: {
      type: String,
      required: true,
    },
    eventRegister: {
      type: String,
      required: true,
    },
    sendEmailOtpUrl: String,
    verifyEmailOtpUrl: String,
    sendPhoneOtpUrl: String,
    verifyPhoneOtpUrl: String,
  },

  data() {
    return {
      loading: false,
      captchaToken: null,
      captchaError: false,
      errors: {},
      step: "details",
      token: "",
      eventConfig: { accepts_abstract: false, abstract_required: false },
      abstractFile: null,
      deliveryMethod: "whatsapp",
      otpDigits: ["", "", "", ""],
      expiresAt: "",
      secondsRemaining: 0,
      cooldownRemaining: 0,
      timer: null,
      cooldownTimer: null,
      otpError: "",
      pricingList: [],
      selectedCategory: "",
      selectedSubCategory: "",
      form: {
        name: "",
        email: "",
        phone_no: "",
        country_code: "91",
        university_name: "",
        programme: "",
        year_semester_of_study: "",
        title_of_paper: "",
        name_of_co_author: "",
        institution_of_co_authors: "",
        certificate_name: "",
        certificate_affiliation: "",
      },
    };
  },

  mounted() {
    window.onCaptchaVerified = this.onCaptchaVerified;
    this.fetchPricing();
  },

  watch: {
    selectedCategory() {
      this.selectedSubCategory = "";
    },
  },

  computed: {
    categories() {
      return [...new Set(this.pricingList.map((p) => p.category_name))];
    },
    subCategories() {
      return this.pricingList
        .filter((p) => p.category_name === this.selectedCategory)
        .map((p) => p.sub_category_name)
        .filter((v, i, a) => a.indexOf(v) === i);
    },
    selectedPricing() {
      if (!this.selectedCategory || !this.selectedSubCategory) {
        return null;
      }
      return this.pricingList.find(
        (p) =>
          p.category_name === this.selectedCategory &&
          p.sub_category_name === this.selectedSubCategory
      ) || null;
    },
    otpCode() { return this.otpDigits.join(""); },
    expired() { return this.secondsRemaining <= 0; },
    maskedEmail() { const p=this.form.email.split('@'); if(p.length<2)return this.form.email; return p[0].slice(0,2)+'*'.repeat(Math.max(p[0].length-2,1))+'@'+p[1]; },
    deliveryLabel() { return this.deliveryMethod === 'whatsapp' ? 'WhatsApp' : 'SMS'; },
    formattedTime() { const m=Math.floor(this.secondsRemaining/60),s=this.secondsRemaining%60; return String(m).padStart(2,'0')+':'+String(s).padStart(2,'0'); },
    resendLabel() { if(!this.expired)return 'Resend in '+this.formattedTime; if(this.cooldownRemaining>0)return 'Resend in 00:'+String(this.cooldownRemaining).padStart(2,'0'); return 'Resend OTP'; },
    isFreePricing() {
      return (
        this.selectedPricing &&
        (this.selectedPricing.is_free ||
          Number(this.selectedPricing.amount) === 0)
      );
    },
    isGstApplicable() {
      return this.selectedPricing && this.selectedPricing.is_gst_applicable;
    },
    gstRate() {
      return this.selectedPricing ? Number(this.selectedPricing.gst_rate) : 0;
    },
    totalAmount() {
      return this.selectedPricing ? Number(this.selectedPricing.amount) : 0;
    },
    baseAmount() {
      if (!this.isGstApplicable) return this.totalAmount;
      return Math.round((this.totalAmount * 100) / (100 + this.gstRate));
    },
    gstAmount() {
      if (!this.isGstApplicable) return 0;
      return this.totalAmount - this.baseAmount;
    },
  },

  methods: {
    async fetchPricing() {
      this.loading = true;
      try {
        const res = await axios.get(this.pricingApi);
        this.pricingList = res.data.data || [];
        this.eventConfig = res.data.event || this.eventConfig;
      } catch (e) {
        console.error("Pricing API error", e);
      } finally {
        this.loading = false;
      }
    },

    onCaptchaVerified(token) {
      this.captchaToken = token;
      this.captchaError = false;
    },

    getError(field) {
      return this.errors[field] && this.errors[field][0] ? this.errors[field][0] : "";
    },

    hasError(field) {
      return this.errors[field] && this.errors[field].length > 0;
    },

    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field);
      }
    },

    setErrors(errorsObj) {
      this.errors = errorsObj || {};
    },

    async submitForm() {
      this.setErrors({});

      const requiredFields = [
        ['name', 'Name'],
        ['email', 'E-mail Address'],
        ['phone_no', 'Mobile Number'],
        ['university_name', 'Name of University'],
        ['programme', 'Programme'],
        ['year_semester_of_study', 'Year / Semester of Study'],
        ['title_of_paper', 'Title of Paper'],
        ['certificate_name', 'Name to appear on Certificate'],
        ['certificate_affiliation', 'Affiliation to appear on Certificate'],
      ];
      const missing = requiredFields.find(([key]) => !this.form[key] || !String(this.form[key]).trim());
      if (missing) {
        Swal.fire(`Please fill in: ${missing[1]}`, "", "info");
        return;
      }

      if (!this.pricingList.length) {
        this.setErrors({ pricing_id: ["Registration pricing is not configured for this event."] });
        Swal.fire("Registration is not available right now.", "", "warning");
        return;
      }

      if (!this.selectedPricing) {
        this.setErrors({ pricing_id: ["Please select category and sub category."] });
        Swal.fire("Please select category and sub category.", "", "info");
        return;
      }

      if (!this.captchaToken) {
        this.captchaError = true;
        Swal.fire("Please verify that you are not a robot.", "", "warning");
        return;
      }

      const payload = {
        name: this.form.name,
        email: this.form.email,
        phone_no: this.form.phone_no,
        country_code: this.form.country_code,
        university_name: this.form.university_name,
        programme: this.form.programme,
        year_semester_of_study: this.form.year_semester_of_study,
        title_of_paper: this.form.title_of_paper,
        name_of_co_author: this.form.name_of_co_author,
        institution_of_co_authors: this.form.institution_of_co_authors,
        certificate_name: this.form.certificate_name,
        certificate_affiliation: this.form.certificate_affiliation,
        pricing_id: this.selectedPricing.pricing_id,
        base_amount: this.baseAmount,
        gst_rate: this.gstRate,
        gst_amount: this.gstAmount,
        total_amount: this.totalAmount,
        recaptcha_token: this.captchaToken,
      };

      const formData = new FormData();
      Object.keys(payload).forEach(key => formData.append(key, payload[key] == null ? '' : payload[key]));
      if (this.abstractFile) formData.append('abstract_file', this.abstractFile);
      if (this.token) formData.append('draft_token', this.token);
      this.loading = true;

      try {
        const response = await axios.post(this.eventRegister, formData);
        this.token = response.data.data.token;
        this.step = 'email';
        this.applyChallenge(response.data.data.email_otp);
      } catch (error) {
        if (error.response && error.response.data) {
          const data = error.response.data;
          if (data.errors && typeof data.errors === "object") {
            this.setErrors(data.errors);
            this.$nextTick(() => {
              const firstInvalid = this.$el.querySelector(".is-invalid");
              if (firstInvalid) {
                firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            });
          }
          Swal.fire(
            data.message || "Submission failed",
            "",
            "warning"
          );
        } else {
          Swal.fire("Something went wrong. Please try again.", "", "warning");
        }
      } finally {
        this.loading = false;
      }
    },
    applyChallenge(data) {
      this.expiresAt=data.expires_at; this.deliveryMethod=data.delivery_method||this.deliveryMethod; this.otpDigits=["","","",""]; this.otpError="";
      clearInterval(this.timer); clearInterval(this.cooldownTimer); this.tick(); this.timer=setInterval(this.tick,1000);
      this.cooldownRemaining=data.resend_available_in||0; this.cooldownTimer=setInterval(()=>{this.cooldownRemaining=Math.max(0,this.cooldownRemaining-1)},1000);
      this.$nextTick(()=>this.focusOtp(0));
    },
    tick() { this.secondsRemaining=Math.max(0,Math.ceil((new Date(this.expiresAt).getTime()-Date.now())/1000)); if(!this.secondsRemaining)clearInterval(this.timer); },
    focusOtp(i) { const r=this.$refs['otp'+i]; if(r&&r[0])r[0].focus(); },
    inputOtp(i,e) { const v=e.target.value.replace(/\D/g,'').slice(-1); this.$set(this.otpDigits,i,v); e.target.value=v; this.otpError=''; if(v&&i<3)this.focusOtp(i+1); },
    otpKeydown(i,e) { if(e.key==='Backspace'&&!this.otpDigits[i]&&i>0){this.$set(this.otpDigits,i-1,'');this.focusOtp(i-1);} },
    pasteOtp(e) { const c=(e.clipboardData.getData('text')||'').replace(/\D/g,'').slice(0,4); if(c.length===4){this.otpDigits=c.split('');this.focusOtp(3);} },
    async verifyOtp() {
      this.loading=true; this.otpError='';
      try { const url=this.step==='email'?this.verifyEmailOtpUrl:this.verifyPhoneOtpUrl; const r=await axios.post(url,{token:this.token,otp:this.otpCode}); if(this.step==='email'){this.step='delivery';this.otpDigits=["","","",""];}else window.location.href=r.data.data.redirect_url; }
      catch(e){this.handleOtpError(e);} finally{this.loading=false;}
    },
    async sendPhoneOtp() { this.loading=true;this.otpError='';try{const r=await axios.post(this.sendPhoneOtpUrl,{token:this.token,delivery_method:this.deliveryMethod});this.step='phone';this.applyChallenge(r.data.data);}catch(e){this.handleOtpError(e);}finally{this.loading=false;} },
    async resendOtp() { this.loading=true;this.otpError='';try{const url=this.step==='email'?this.sendEmailOtpUrl:this.sendPhoneOtpUrl;const body={token:this.token};if(this.step==='phone')body.delivery_method=this.deliveryMethod;const r=await axios.post(url,body);this.applyChallenge(r.data.data);}catch(e){this.handleOtpError(e);}finally{this.loading=false;} },
    handleOtpError(e) { const d=e.response&&e.response.data?e.response.data:{};this.otpError=d.message||'Something went wrong. Please try again.';if(d.data&&d.data.retry_after!==undefined)this.cooldownRemaining=d.data.retry_after;this.otpDigits=["","","",""]; },
  },
};
</script>

<style scoped>
.registration-form {
  --reg-primary: #1e40af;
  --reg-primary-dark: #1e3a8a;
  --reg-border: #e2e8f0;
  --reg-muted: #64748b;
  --reg-text: #0f172a;
  --reg-surface: #f8fafc;
  padding: 4px 2px 8px;
  color: var(--reg-text);
}

.form-intro {
  font-size: 0.92rem;
  margin-bottom: 1.25rem;
  color: var(--reg-muted);
  line-height: 1.5;
}

.reg-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--reg-border);
}

.reg-section-last {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.reg-section-heading {
  font-size: 1rem;
  font-weight: 700;
  color: var(--reg-text);
  margin-bottom: 1rem;
  padding-left: 12px;
  border-left: 3px solid var(--reg-primary);
}

.reg-section-sub {
  font-size: 0.88rem;
  margin-top: -0.35rem;
  margin-bottom: 1rem;
  color: var(--reg-muted);
}

.registration-form label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #334155;
  margin-bottom: 0.35rem;
}

.optional-tag {
  font-weight: 400;
  color: var(--reg-muted);
  font-size: 0.82rem;
}

.registration-form .form-control,
.registration-form .input-group-text {
  border: 1px solid var(--reg-border);
  border-radius: 8px;
  min-height: 44px;
  font-size: 0.95rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.registration-form .form-control:focus {
  border-color: var(--reg-primary);
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.12);
}

.registration-form select.form-control {
  height: 44px;
}

.registration-form .form-group {
  margin-bottom: 1rem;
}

.file-upload-wrap {
  padding: 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: var(--reg-surface);
}

.selected-file {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #15803d;
}

.pricing-breakup {
  margin-top: 0.25rem;
  padding: 1rem 1.1rem;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #eff6ff;
}

.pricing-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.92rem;
  color: #334155;
  padding: 0.2rem 0;
}

.pricing-row-total {
  margin-top: 0.35rem;
  padding-top: 0.65rem;
  border-top: 1px solid #bfdbfe;
  font-weight: 700;
  color: var(--reg-text);
}

.pricing-row-total strong {
  color: #15803d;
}

.pricing-notes {
  margin: 0.75rem 0 0;
  font-size: 0.82rem;
  color: var(--reg-muted);
}

.captcha-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.25rem;
}

.submit-wrap {
  text-align: center;
  margin-top: 0.5rem;
}

.btn-verify-primary,
.btn-verify-primary span,
.btn-verify-primary i {
  color: #fff;
}

.btn-verify-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 320px;
  min-height: 48px;
  padding: 0 24px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(180deg, var(--reg-primary) 0%, var(--reg-primary-dark) 100%);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  box-shadow: 0 4px 14px rgba(30, 64, 175, 0.25);
}

.btn-verify-primary:hover:not(:disabled),
.btn-verify-primary:hover:not(:disabled) span,
.btn-verify-primary:hover:not(:disabled) i {
  color: #fff;
}

.btn-verify-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(30, 64, 175, 0.3);
}

.btn-verify-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.verify-flow {
  max-width: 480px;
  margin: 0 auto;
  padding: 0.5rem 0 1rem;
}

.verify-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 1.75rem;
}

.verify-progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 72px;
}

.step-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e2e8f0;
  color: var(--reg-muted);
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.2s ease;
}

.verify-progress-step.active .step-dot,
.verify-progress-step.done .step-dot {
  background: var(--reg-primary);
  color: #fff;
}

.step-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--reg-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.verify-progress-step.active .step-label,
.verify-progress-step.done .step-label {
  color: var(--reg-primary);
}

.verify-progress-line {
  width: 64px;
  height: 2px;
  background: #e2e8f0;
  margin: 0 8px 18px;
  transition: background 0.2s ease;
}

.verify-progress-line.done {
  background: var(--reg-primary);
}

.verify-card {
  text-align: center;
  padding: 0.25rem 0.5rem;
}

.verify-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

.verify-icon-email {
  background: #eff6ff;
  color: var(--reg-primary);
}

.verify-icon-phone {
  background: #f0fdf4;
  color: #15803d;
}

.verify-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--reg-text);
  margin-bottom: 0.5rem;
}

.verify-subtitle {
  font-size: 0.92rem;
  color: var(--reg-muted);
  line-height: 1.55;
  margin-bottom: 1.5rem;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
}

.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 0 0 1rem;
}

.otp-box {
  width: 54px;
  height: 58px;
  text-align: center;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--reg-text);
  border: 1.5px solid var(--reg-border);
  border-radius: 10px;
  background: #fff;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.otp-box:focus {
  border-color: var(--reg-primary);
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.12);
}

.otp-box:disabled {
  background: var(--reg-surface);
  color: var(--reg-muted);
}

.verify-timer {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  background: var(--reg-surface);
  color: var(--reg-muted);
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 1.25rem;
}

.verify-timer.expired {
  background: #fef2f2;
  color: #dc2626;
}

.verify-alert {
  margin-bottom: 1rem;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.9rem;
  text-align: left;
}

.verify-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 1rem;
}

.btn-verify-link {
  background: none;
  border: none;
  color: var(--reg-primary);
  font-size: 0.88rem;
  font-weight: 600;
  padding: 6px 10px;
  cursor: pointer;
}

.btn-verify-link:disabled {
  color: #94a3b8;
  cursor: not-allowed;
}

.delivery-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 1.5rem;
}

.delivery-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 1.1rem 0.75rem;
  border: 1.5px solid var(--reg-border);
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  text-align: center;
}

.delivery-card:hover {
  border-color: #94a3b8;
}

.delivery-card.active {
  border-color: var(--reg-primary);
  background: #f8faff;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}

.delivery-card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 1.25rem;
  margin-bottom: 2px;
}

.delivery-card-icon.whatsapp {
  background: #dcfce7;
  color: #16a34a;
}

.delivery-card-icon.sms {
  background: #eff6ff;
  color: var(--reg-primary);
}

.delivery-card-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--reg-text);
}

.delivery-card-desc {
  font-size: 0.75rem;
  color: var(--reg-muted);
  line-height: 1.35;
}

@media (max-width: 767px) {
  .delivery-grid {
    grid-template-columns: 1fr;
  }

  .otp-box {
    width: 48px;
    height: 52px;
    font-size: 1.2rem;
  }

  .btn-verify-primary {
    max-width: none;
  }
}
</style>
